/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { useSiteAlerts } from "../hooks/useSiteAlerts"
import { useGlobalState } from "./GlobalStateProvider"
import { useSettings } from "../utils/useSettings"
import React, { useEffect, useMemo, useState } from "react"
import { isProductExported } from "../stories/quoteOrders/quoteOrderUtils"
import { alternativeProductAttribute } from "../hooks/useProductAlternative"
import { useLiftedModal } from "./ModalProvider"
import { Grid, TextGroupMedium } from "@cd2/components"
import { P } from "@cd2/text"
import { ButtonsWrapper } from "./ButtonsWrapper"
import { CancelButton } from "./Buttons/CancelButton"
import { Fields } from "@cd2/forms"
import { themeColors } from "./EOSThemeProvider"
import { rankProductSearchResults } from "../utils/productSearchRanking"
import { IProductLocationStockResponseItem, useApi } from "../hooks/useApi"
import { combineProductLocationStockData } from "../apiHooks/productLocations"
import { useQuery } from "@tanstack/react-query"
import { IRecord } from "../types/schema"
import { SageApiExportProductToSage } from "./SageApiExport/SageApiExportProductToSage"
import {
  fetchSalesRestrictedProductIds,
  isProductSalesRestrictedForCustomer,
} from "../constants/salesRestrictedProductFilter"

type ISelectListProduct = {
  active: boolean
  available_stock: number
  body: string
  id: string
  is_consignment: string
  legacy_stock_code: string
  stock_code: string
  sage_id: string
  salesRestrictedForCustomer?: boolean
}

type ISelectedProduct = IRecord<
  "products",
  | "stock_code"
  | "body"
  | "cost_price_pennies"
  | "preferred_supplier_list_price_pennies"
  | "sage_id"
  | "active"
  | "product_master"
> & {
  legacy_stock_code?: string
  line_item_description?: string
  product_locations: IRecord<"product_locations", "name" | "free_stock" | "warehouse">[]
  alternative?: { id: string } | string
  is_consignment?: boolean
  stockLevels?: { availableInStock?: number }
  salesRestrictedForCustomer?: boolean
  product_group?: {
    product_group_master?: {
      sales_restricted?: boolean
      allowed_sales_customers?: { id: string }[]
    }
  }
}

// Stale responses are dropped (see state.query in handleLoadOptions), so a short debounce is safe.
const SEARCH_DEBOUNCE_MS = 100

export function ProductSelect2(props: {
  value?: ISelectedProduct | string | null
  onChange?(value: ISelectedProduct): void
  error?: string
  autoFocus?: boolean
  disabled?: boolean
  hideStock?: boolean
  label?: string
  filter?: Record<string, any>
  isClearable?: boolean
  showRequiredStar?: boolean
  preventSelectUnexported?: boolean
  branchId?: string
  customerId?: string
}) {
  const api = useApi()
  const alert = useSiteAlerts()
  const { currentUser } = useGlobalState()
  const { stockWarehouses, warehouses } = useSettings()
  const skeem = window.skeem
  const [product, setProduct] = useState<ISelectedProduct | null>(() => {
    if (props.value && typeof props.value === "object") {
      return props.value
    } else {
      return null
    }
  })
  const [state] = useState<{ query: string; options: ReturnType<typeof selectLabel>[] }>({
    query: "",
    options: [],
  })

  useEffect(() => {
    if (!props.value) {
      setProduct(null)
    } else if (typeof props.value === "string") {
      if (!product || (product && props.value !== product?.id)) {
        setProduct(null)
        loadProduct(props.value).then(setProduct)
      }
    } else if (props.value && props.value.id !== product?.id) {
      setProduct(props.value)
    }
  }, [props.value])

  function selectLabel(
    product: ISelectListProduct | ISelectedProduct | null,
    isSelected: boolean,
    query = "",
  ) {
    if (!product) {
      return {
        label: <span css={css("color: #ccc")}>Select product...</span>,
        value: "",
      }
    }

    const notActiveMessage =
      product.active === false ? <i css={css("color: #666")}> (Product is inactive)</i> : ""
    const notExportedMessage = !isProductExported(product) ? (
      <i css={css("color: #666")}> (Not exported)</i>
    ) : (
      ""
    )
    const salesRestrictedMessage = product.salesRestrictedForCustomer ? (
      <i css={css("color: #666")}> (Sales restricted)</i>
    ) : (
      ""
    )

    if (isSelected) {
      const opt = product as ISelectedProduct
      const label = `${opt.stock_code}: ${opt.body}`

      const hasAlternativeMessage = opt.alternative ? (
        <small>
          <i>Mandatory product available</i>
        </small>
      ) : (
        ""
      )

      return {
        label: (
          <div>
            <p
              css={css`
                font-size: 12px;
                font-weight: 600;
                margin: 0;
              `}
            >
              {label}
              {!props.hideStock && (
                <React.Fragment>
                  {" "}
                  (
                  <StockLevelForProduct productLocations={opt.product_locations.map(l => l.id)} />)
                </React.Fragment>
              )}
              {notExportedMessage}
              {notActiveMessage}
              {salesRestrictedMessage}
            </p>
            {hasAlternativeMessage}
          </div>
        ),
        value: product.id,
        active: product.active,
        salesRestrictedForCustomer: product.salesRestrictedForCustomer,
        product,
      }
    }

    const opt = product as ISelectListProduct

    return {
      label: (
        <div>
          <p
            css={css`
              font-size: 12px;
              font-weight: 600;
              margin: 0;
            `}
          >
            {highlight(opt.stock_code, query)}: {highlight(opt.body, query)}
            {!props.hideStock && ` (${opt.available_stock} in stock)`}
            {notExportedMessage}
            {notActiveMessage}
            {salesRestrictedMessage}
            {opt.legacy_stock_code &&
              opt.legacy_stock_code.toUpperCase() !== opt.stock_code.toUpperCase() && (
                <i css={css("color: #666")}>
                  <br />
                  Previously{" "}
                  <span css={css("font-weight: 600")}>
                    {highlight(opt.legacy_stock_code, query)}
                  </span>
                </i>
              )}
          </p>
        </div>
      ),
      value: opt.id,
      active: opt.active,
      salesRestrictedForCustomer: opt.salesRestrictedForCustomer,
      product,
    }
  }

  const productAttributes = [
    "stock_code",
    "legacy_stock_code",
    "body",
    "cost_price_pennies",
    "preferred_supplier_list_price_pennies",
    "line_item_description",
    "sage_id",
    "active",
    "is_consignment",
    "product_master",
    { name: "product_locations", attributes: ["name", "free_stock", "warehouse"] },
    alternativeProductAttribute,
    ...(props.customerId
      ? [
          {
            name: "product_group",
            attributes: [
              {
                name: "product_group_master",
                attributes: ["sales_restricted", "allowed_sales_customers"],
              },
            ],
          },
        ]
      : []),
  ]

  async function loadProduct(id: string) {
    const [product] = await skeem.fetch<ISelectedProduct>({
      products: {
        attributes: productAttributes,
        filter: { matches: { id: id ?? "" } },
      },
    })
    if (product && props.customerId) {
      product.salesRestrictedForCustomer = isProductSalesRestrictedForCustomer(
        product,
        props.customerId,
      )
    }
    return product
  }

  async function handleLoadOptions(query: string) {
    state.query = query

    const warehouseNames =
      stockWarehouses && stockWarehouses.length > 0
        ? stockWarehouses.map(w => w.name)
        : warehouses.map(w => w.name)

    const search = await api.searchProductsForSelect(
      {
        query,
        branchId: props.branchId || currentUser.branch.id!,
        warehouseNames,
      },
      { retries: 3 },
    )

    if (!search.success) {
      alert.genericError("Error loading products")
      return []
    }
    const products = search.data

    let restrictedIds = new Set<string>()
    if (props.customerId && products.length > 0) {
      restrictedIds = await fetchSalesRestrictedProductIds(
        skeem,
        products.map(product => product.id),
        props.customerId,
      )
    }

    if (state.query === query) {
      state.options = rankProductSearchResults(products, query).map(product =>
        selectLabel(
          {
            ...product,
            salesRestrictedForCustomer: restrictedIds.has(product.id),
          },
          false,
          query,
        ),
      )
    }
    return state.options
  }

  const debouncedLoadOptions = useMemo(() => {
    let debounceTimeout: any
    return (query: string, callback: (results: any[]) => void) => {
      clearTimeout(debounceTimeout)

      debounceTimeout = setTimeout(() => {
        handleLoadOptions(query).then(callback)
      }, SEARCH_DEBOUNCE_MS)
    }
  }, [
    JSON.stringify(stockWarehouses),
    JSON.stringify(props.filter),
    props.branchId || currentUser.branch.id,
    props.customerId,
  ])

  async function handleSelect(id: string) {
    const product = await loadProduct(id)
    if (product?.salesRestrictedForCustomer) {
      alert.genericError("This product is sales restricted for this customer")
      return
    }
    props.onChange?.(product)
    setProduct(product)
  }

  const { closeModal: closeExportModal, openModal: openExportModal } = useLiftedModal<{
    product: ISelectListProduct
    value: string
  }>({
    render: data => (
      <div>
        <Grid gap={8}>
          <TextGroupMedium heading={`${data.product?.stock_code} has not been exported`} />
          <P>To use this product, it needs to be exported to sage first.</P>
          {currentUser.canExportNewProducts && (
            <SageApiExportProductToSage
              productId={data.product?.id ?? ""}
              afterExport={console.log}
              compact
            />
          )}
          <ButtonsWrapper>
            <CancelButton onClick={closeExportModal}>Close</CancelButton>
          </ButtonsWrapper>
        </Grid>
      </div>
    ),
    onRequestClose: "default",
    isOpen: true,
  })
  const valueProduct = product ? selectLabel({ ...product }, true, "") : null
  return (
    <Fields.AsyncSelect
      options={valueProduct ? [valueProduct] : []}
      placeholder="Select product..."
      value={valueProduct}
      onChange={(val: any) => handleSelect(val?.value)}
      loadOptions={debouncedLoadOptions}
      autoFocus={props.autoFocus}
      error={props.error}
      disabled={props.disabled}
      isOptionDisabled={opt =>
        opt.active === false ||
        !!opt.salesRestrictedForCustomer ||
        !!(props.preventSelectUnexported && !isProductExported(opt.product))
      }
      label={
        props.showRequiredStar ? (
          <span>
            {props.label} <span style={{ color: themeColors.alertRed }}>*</span>
          </span>
        ) : (
          props.label
        )
      }
      noOptionsMessage={({ inputValue }: { inputValue: string }) =>
        inputValue.length > 0
          ? `No products found for search query: ${inputValue}`
          : "Start typing to search for products"
      }
      styles={{
        dropdownIndicator: (base: any) => ({ ...base, padding: 8 }),
        valueContainer: (base: any) => ({ ...base, padding: 8 }),
      }}
      formatOptionLabel={opt => (
        <div
          onClick={
            opt.salesRestrictedForCustomer
              ? undefined
              : opt.active === false ||
                  (props.preventSelectUnexported && !isProductExported(opt.product))
                ? () => openExportModal(opt)
                : undefined
          }
        >
          {opt.label}
        </div>
      )}
      isClearable={props.isClearable}
      css={css`
        min-width: 200px;
      `}
      // cacheOptions
    />
  )
}

export function highlight(
  text: string,
  query: string,
  opts?: { wrap: (textEl: string) => React.ReactNode },
) {
  const queryParts = query.toUpperCase().trim().split(/\s+/)

  const outputParts: React.ReactNode[] = []

  let j = 0
  for (let i = 0; i < text.length; i++) {
    const match = queryParts.find(part => text.toUpperCase().slice(i, i + part.length) === part)
    if (match) {
      outputParts.push(text.slice(j, i))
      outputParts.push(
        opts?.wrap ? (
          opts.wrap(text.slice(i, i + match.length))
        ) : (
          <u>{text.slice(i, i + match.length)}</u>
        ),
      )
      j = i + match.length
    }
  }
  outputParts.push(text.slice(j))

  return <React.Fragment>{outputParts}</React.Fragment>
}

function StockLevelForProduct(props: { productLocations: string[] }) {
  const api = useApi()
  const { data, isPending, error } = useQuery({
    queryKey: ["productSelectStock", props.productLocations],
    queryFn: async () => {
      let stock: Record<string, IProductLocationStockResponseItem> = {}
      if (props.productLocations.length > 0) {
        stock = await api.productLocationsStock(props.productLocations)
      }
      return combineProductLocationStockData(
        props.productLocations.map(l => stock[l] ?? null).filter(Boolean),
      )
    },
  })

  if (isPending) {
    return <span css={css("color: #666")}>Loading...</span>
  }

  if (error) {
    return <span css={css("color: #666")}>Error loading stock</span>
  }

  return (
    <span css={css("color: #666")}>{data ? data.inStock - data.allocatedStock : "0"} in stock</span>
  )
}
